import React from 'react'
import { hasValue } from '~/utils/common'

const getDotNotatedObjectKeys = (object: Record<string, unknown>) => {
  const keys = Object.keys(object)
  const dotNotatedKeys = keys.map((key) => {
    if (typeof object[key] === 'object') {
      const subKeys = getDotNotatedObjectKeys(
        object[key] as Record<string, unknown>,
      )
      return subKeys.map((subKey) => `${key}.${subKey}`)
    }
    return key
  })
  return dotNotatedKeys.flat()
}

const buildDotNotatedObjectByKeyString = (
  parentObject: Record<string, unknown>,
  keyString: string,
  value: string,
) => {
  const keySplits = keyString.split('.')
  let object = parentObject
  for (let i = 0; i < keySplits.length; i++) {
    const key = keySplits[i]
    if (i < keySplits.length - 1) {
      if (!object[key]) {
        object[key] = {}
      }
      object = object[key] as Record<string, unknown>
      continue
    }

    object[key] = value
  }
  return object
}

export const getDotWalkedObjectMap = (
  object: Record<string, unknown>,
  depthCutoff = 0,
) => {
  const dotNotatedKeys = getDotNotatedObjectKeys(object)
  const depthCutoffKeyHashSet = new Set<string>()
  dotNotatedKeys.map((key) => {
    const keySplits = key.split('.')
    let rebuiltKey = keySplits[0]
    for (let i = 1; i < keySplits.length - depthCutoff; i++) {
      rebuiltKey = `${rebuiltKey}.${keySplits[i]}`
    }
    depthCutoffKeyHashSet.add(rebuiltKey)
  })
  const parentObject = {}
  depthCutoffKeyHashSet.forEach((key) => {
    buildDotNotatedObjectByKeyString(parentObject, key, key)
  })

  return parentObject
}

export const getShallowObjectKeyMap = (object: Record<string, unknown>) => {
  const keys = Object.keys(object)
  const keyMap = {}
  keys.forEach((key) => {
    keyMap[key] = key
  })
  return keyMap
}

export const getName = <T extends PropertyKey>(object: SingleKeyObject<T>) => {
  return Object.keys(object)[0]
}

export const getOutOfSyncProperties = <T>(
  source: Partial<T>,
  target: Partial<T>,
): Partial<T> => {
  const outOfSyncProperties: Partial<T> = {}
  for (const key in source) {
    const sourceValue = source[key]
    const targetValue = target[key]

    const sourceIsObject =
      sourceValue !== null &&
      typeof sourceValue === 'object' &&
      !(sourceValue instanceof Date)

    const targetIsObject =
      targetValue !== null &&
      typeof targetValue === 'object' &&
      !(targetValue instanceof Date)

    if (sourceIsObject && targetIsObject) {
      const outOfSyncSubProperties = getOutOfSyncProperties(
        sourceValue as Record<string, unknown>,
        targetValue as Record<string, unknown>,
      )

      if (!isEmptyObject(outOfSyncSubProperties)) {
        outOfSyncProperties[key] = outOfSyncSubProperties as T[Extract<
          keyof T,
          string
        >]
      }

      continue
    }

    if (sourceValue !== targetValue) {
      outOfSyncProperties[key] = sourceValue as T[Extract<keyof T, string>]
    }
  }

  return outOfSyncProperties
}

export const isEmptyObject = (object: Record<string, unknown> | unknown) => {
  if (typeof object !== 'object') return undefined

  return Object.keys(object!).length === 0
}

export const isEmptyOrHasNoValue = (object?: Record<string, unknown>) => {
  return isEmptyObject(object) || !hasValue(object)
}

export const hasKey = <T extends object>(object: T, key: keyof T) => {
  return Object.prototype.hasOwnProperty.call(object, key)
}

export const getValues = <T extends object>(object: T): T[keyof T][] => {
  return Object.values(object) as T[keyof T][]
}

export const areValuesEqual = <T>(a: T, b: T): boolean => {
  // Handle primitive types and null/undefined
  if (a === b) return true
  if (a === null || b === null) return false
  if (a === undefined || b === undefined) return false

  // Handle different types
  if (typeof a !== typeof b) return false

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => areValuesEqual(item, b[index]))
  }

  // Handle Maps
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false
    for (const [key, value] of a) {
      if (!b.has(key)) return false
      if (!areValuesEqual(value, b.get(key))) return false
    }
    return true
  }

  // Handle Sets
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false
    for (const item of a) {
      if (!b.has(item)) return false
    }
    return true
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)

    if (keysA.length !== keysB.length) return false

    return keysA.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(b, key) &&
        areValuesEqual(a[key], b[key]),
    )
  }

  return false
}
/**
 * Get the text content of a node.
 * @param node - The node to get the text content of.
 * @returns The text content of the node.
 */
export const getNodeText = (
  node?: string | React.ReactNode | number,
): string => {
  if (!node) return ''
  if (node instanceof Array) return node.map(getNodeText).join(' ')
  if (React.isValidElement(node)) return getNodeText(node.props.children)
  return String(node)
}
